import type { ReactNode, ReactElement, JSX } from "react";
import { cn } from "@repo/lib";
import type { LinkProps } from "@tanstack/react-router";
import {
  DrawerRoot,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
} from "@/components/drawer";
import { ListRoot, Item, ItemContent, ItemMainContent } from "@/components/list";

export interface SelectDrawerItemProps<T extends string | number> {
  value: T;
  label: string;
}

interface MenuDrawerProps {
  children: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const MenuDrawer = ({ open, setOpen, children }: MenuDrawerProps): JSX.Element => {
  return (
    <DrawerRoot onClose={() => setOpen(false)} open={open} setOpen={setOpen}>
      <DrawerContent>{children}</DrawerContent>
    </DrawerRoot>
  );
};

interface MenuDrawerTitleProps {
  children: ReactNode;
}

export const MenuDrawerTitle = ({ children }: MenuDrawerTitleProps): JSX.Element => (
  <DrawerHeader>
    <DrawerTitle>{children}</DrawerTitle>
  </DrawerHeader>
);

interface MenuDrawerBodyProps {
  className?: string;
  children: ReactElement;
}

const MenuDrawerBody = ({ className, children }: MenuDrawerBodyProps): JSX.Element => {
  return (
    <DrawerBody>
      <div
        className={cn(
          "text-md font-normal text-neutral-500 max-w-md w-full mx-auto flex flex-col overflow-auto",
          className,
        )}
      >
        <ul className="grid grid-cols-1 divide-y">{children}</ul>
      </div>
    </DrawerBody>
  );
};

interface MenuDrawerBodyListProps {
  children?: ReactElement[];
}

export const MenuDrawerBodyList = ({
  children,
}: MenuDrawerBodyListProps): JSX.Element => {
  return (
    <MenuDrawerBody>
      <ListRoot className="divide-y-0">{children}</ListRoot>
    </MenuDrawerBody>
  );
};

interface MenuDrawerBodyListItemProps {
  children: string;
  linkProps?: LinkProps;
}

export const MenuDrawerBodyListItem = ({
  children,
  linkProps,
}: MenuDrawerBodyListItemProps): ReactElement => {
  return (
    <Item
      className="py-6 px-4 w-full bg-gray-100 rounded-lg"
      spacing="small"
      linkProps={linkProps}
    >
      <ItemContent>
        <ItemMainContent>{children}</ItemMainContent>
      </ItemContent>
    </Item>
  );
};
